import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { TimeField } from '../../../../components/dateFields';

/**
 * Two jobs, one dialog.
 *
 * 1. The original: HR fills in a punch (or a pair) that was never recorded.
 * 2. The one that was missing, and cost real money: when the day ALREADY has a clock-in with no
 *    clock-out, this dialog used to open blank — as if nothing had been entered — so HR entered a
 *    second clock-in and a duplicate/overlapping row was created instead of the first one being
 *    finished. The unfinished row then sat until the stale-shift job closed it at zero duration and
 *    the hours were paid as 0.00.
 *
 * So when `record.openShift` is present the dialog stops being an "add" form. It states what is
 * already on the record, takes the one field that is genuinely missing, and its CTA names that
 * outcome. There is no path from here to a second row.
 */
const AddMissingLogModal = ({
  open,
  record,
  onClose,
  onSave,
  saving = false,
}) => {
  const { t } = useTranslation();
  const [inTime, setInTime] = useState('');
  const [outTime, setOutTime] = useState('');
  const [comments, setComments] = useState('');

  const openShift = record?.openShift || null;

  useEffect(() => {
    if (!open) return;
    setInTime(record?.clockIn && record?.clockIn !== '-' ? String(record.clockIn).slice(0, 5) : '');
    setOutTime(record?.clockOut && record?.clockOut !== '-' ? String(record.clockOut).slice(0, 5) : '');
    setComments('');
  }, [open, record?.id]);

  if (!open) return null;

  // In completion mode the clock-in is a fact, not an input, so only the clock-out can block submit.
  const missingRequiredTime = openShift ? !outTime : (!inTime && !outTime);
  const canSubmit = !missingRequiredTime && !saving;

  const dismiss = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-foreground mb-1">
          {openShift ? t('Complete attendance entry') : t('Add Missing Log')}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {openShift
            ? `${t('Finish the unclosed entry for')} ${record?.employee}`
            : `${t('Add missing attendance log for')} ${record?.employee}`}
        </p>

        {openShift ? (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <Icon name="CircleDashed" size={16} className="mt-0.5 shrink-0 text-amber-700" />
              <div className="text-xs text-amber-900">
                <p className="font-medium">
                  {t('Clocked in at')} {record?.clockIn}
                  {openShift?.isBackdatedEntry ? ` · ${t('entered manually')}` : ''}
                </p>
                <p className="mt-1">
                  {t('No clock-out was ever recorded. Add it below to close this entry. If it is left unfinished it will be closed automatically with zero hours, and the day will be paid as 0.')}
                </p>
                {openShift?.hasOpenBreak ? (
                  <p className="mt-1 font-medium">
                    {t('This entry also has a break that was never ended. Record the break-out first.')}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Clock In')}</label>
              <TimeField
                value={inTime}
                disabled={!!openShift}
                onChange={(v) => setInTime(v)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Clock Out')}
                {openShift ? <span className="text-destructive"> *</span> : null}
              </label>
              <TimeField
                value={outTime}
                onChange={(v) => setOutTime(v)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('Comments')}</label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e?.target?.value)}
              placeholder={t('Optional comment...')}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={2}
            />
          </div>

          {missingRequiredTime ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              {openShift
                ? t('Enter the clock-out time to close this entry.')
                : t('Provide Clock In and/or Clock Out time.')}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={dismiss}
            disabled={saving}
            className="px-4 py-2 text-sm bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSave?.({ inTime: openShift ? '' : inTime, outTime, comments })}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name={openShift ? 'LogOut' : 'Plus'} size={16} />
            <span>{openShift ? t('Save clock-out') : t('Save')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMissingLogModal;
